import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { ArrowRight, RotateCcw, Trophy } from "lucide-react";
import { useSEO } from "../hooks/useSEO";

const Placement = () => {
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useSEO({
    title: "Test de niveau japonais gratuit : Estime ton JLPT en 2 min | Japonais Pas à Pas",
    description: "Quiz gratuit en 10 questions pour évaluer ton niveau japonais (JLPT N5, N4, N3). Sans inscription, résultat immédiat. Idéal avant de démarrer ou de passer le JLPT.",
    canonical: "https://japonais-pas-a-pas.com/placement",
  });

  useEffect(() => {
    api.get("/quiz/placement").then((r) => setQuestions(r.data.data)).catch(() => {});
  }, []);

  const select = async (optIdx) => {
    const next = [...answers, optIdx];
    setAnswers(next);
    if (idx + 1 < questions.length) {
      setIdx(idx + 1);
    } else {
      setLoading(true);
      try {
        const { data } = await api.post("/quiz/placement/submit", { answers: next });
        setResult(data);
      } finally {
        setLoading(false);
      }
    }
  };

  const reset = () => { setIdx(0); setAnswers([]); setResult(null); };

  if (!questions.length) return <div className="washi-bg min-h-screen"><Navbar /></div>;
  const q = questions[idx];
  const progress = ((idx + (result ? 1 : 0)) / questions.length) * 100;

  return (
    <div className="washi-bg min-h-screen" data-testid="placement-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 sm:px-12 py-16">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">Test de niveau</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1A2A40] mb-3">
          {result ? "Votre niveau" : `Question ${idx + 1} / ${questions.length}`}
        </h1>

        {/* Progress */}
        <div className="h-2 bg-[#F3F6F1] rounded-full overflow-hidden mb-10">
          <div className="h-full bg-[#E94A3F] transition-all" style={{ width: `${progress}%` }} />
        </div>

        {!result && q && (
          <div className="bg-[#FDFBF7] rounded-3xl p-8 sm:p-10 border-2 border-[#1A2A40] shadow-brut animate-fade-up" key={idx} data-testid={`question-${idx}`}>
            <div className="text-xs font-bold uppercase tracking-widest text-[#8CA07A] mb-3">Niveau {q.level}</div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl mb-8 text-[#1A2A40] leading-snug">{q.question}</h2>
            <div className="space-y-3" data-testid="options">
              {q.options.map((o, i) => (
                <button
                  key={i}
                  onClick={() => select(i)}
                  data-testid={`option-${i}`}
                  className="w-full text-left bg-[#FDFBF7] border-2 border-[#1A2A40]/15 rounded-xl px-5 py-4 font-medium text-[#1A2A40] hover:border-[#E94A3F] hover:bg-[#E94A3F] hover:text-white hover-lift inline-flex items-center justify-between group"
                >
                  <span>{o}</span>
                  <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          </div>
        )}

        {loading && <p className="text-center text-[#4A5568]">Analyse de vos réponses...</p>}

        {result && (
          <div className="bg-[#1A2A40] text-white rounded-3xl p-10 border-2 border-[#1A2A40] shadow-brut animate-fade-up" data-testid="result-card">
            <Trophy className="w-12 h-12 text-[#FFD166] mb-4" />
            <div className="text-xs font-bold uppercase tracking-widest text-[#FFD166] mb-2">Votre niveau estimé</div>
            <div className="font-display font-black text-7xl text-[#E94A3F] mb-3">{result.level}</div>
            <div className="text-3xl font-display font-bold mb-4">{result.score} / {result.total} bonnes réponses</div>
            <p className="text-white/80 mb-8 text-lg">{result.message}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/register" data-testid="result-register-btn" className="bg-[#E94A3F] text-white font-bold py-3 px-6 rounded-xl shadow-brut-sm hover-lift inline-flex items-center justify-center gap-2">
                Démarrer mon parcours <ArrowRight className="w-4 h-4" />
              </Link>
              <button onClick={reset} data-testid="result-retry-btn" className="bg-transparent border-2 border-white/30 text-white font-bold py-3 px-6 rounded-xl hover:bg-white/10 inline-flex items-center justify-center gap-2">
                <RotateCcw className="w-4 h-4" /> Recommencer
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Placement;
